/**
 * The error surface both system pages compose.
 *
 * The design system specifies the SHAPE of an error and names no component and
 * no class for it — icon, what happened, what was unaffected, what to do now —
 * so the block rides a data attribute and its kind rides the attribute's own
 * value (`[data-error="server"]`), on the model of `[data-art="wide"]`, rather
 * than a new modifier class.
 *
 * **No status code is printed.** Neither page renders «404» or «500»: a
 * message states the fix and the code goes to the log. The two pages are told
 * apart by their glyph, their heading, their copy, and their action pair, and
 * by nothing else.
 *
 * **No `role="alert"`, and that is a decision rather than an omission.** WCAG
 * 4.1.3 governs a status message injected into a working page; here the error
 * IS the page, and its `<h1>` already says so once. An alert role would make a
 * screen reader read the heading, the copy, and both actions in one
 * uninterruptible burst. What replaces it is the thing a single-page app
 * actually lacks: a route change announces nothing on its own, so the heading
 * takes focus on mount and is announced once, which is also where a keyboard
 * reader wants to be — one Tab from the way out.
 *
 * The whole shell is kept above and below this block. That is the inverse of
 * the usual reason for stripping chrome off an error page: a reader here is
 * more lost than anywhere else, and the shell is the only route out this area
 * owns.
 */
import { useEffect, useRef, type ReactNode } from 'react';

export type ErrorSurfaceKind = 'notfound' | 'server';

export interface ErrorSurfaceProps {
  kind: ErrorSurfaceKind;
  /** Drawn on the design system's own grid at its own weight. */
  glyph: ReactNode;
  heading: string;
  /**
   * What happened and what was NOT affected, in that order. A paid surface
   * names what the reader actually fears for — the account, the subscription,
   * the list — which is the real difference from the same page on the public
   * host, where a visitor has no account to be reassured about.
   */
  body: ReactNode;
  /** Primary first from the reading start, then secondary. */
  actions: ReactNode;
  /** A further route out, under the action pair — the defect-report line. */
  footnote?: ReactNode;
}

/**
 * `.btn--lg` bound to tokens rather than approximated. Full-width at the base
 * tier and auto from `--bp-sm` up: at 320px a shared row puts one of the two
 * under the 44px touch target (WCAG 2.5.8).
 */
const ACTION_BASE =
  'inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 rounded-sm border px-6 py-4 ' +
  'text-body font-semibold no-underline transition-colors duration-fast sm:w-auto';

export const ERROR_ACTION_PRIMARY = `${ACTION_BASE} border-accent bg-accent text-accent-ink hover:bg-accent-hover active:bg-accent-press`;

export const ERROR_ACTION_SECONDARY = `${ACTION_BASE} border-border-strong bg-surface-raised text-ink hover:bg-surface-overlay`;

/** Bound to the text size, never a fixed pixel value, so it scales with the button. */
export const ERROR_ACTION_ICON = 'h-[1.125em] w-[1.125em] shrink-0';

export function ErrorSurface({ kind, glyph, heading, body, actions, footnote }: ErrorSurfaceProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section
      data-error={kind}
      // Alignment is `start`, not `center`: a long centred Arabic column is
      // tiring to read, and a fixed edge at the reading start keeps the
      // heading, the paragraph and the actions on one ruler.
      //
      // The block fills the height it was given and centres inside it, so it
      // does not cling to the top bar above a band of empty background — which
      // reads as a half-loaded page.
      // `w-full` + `mx-auto`: the measure is a CAP, and pinned to the reading
      // edge it left the whole opposite two-thirds of a desktop viewport empty
      // beside a block of text — which reads as a page that failed to load
      // rather than as a deliberately narrow column. The cap is inert below its
      // own width, so nothing moves on a handset. The text inside stays
      // start-aligned: a long centred Arabic column is tiring to read, and a
      // fixed edge at the reading start keeps the heading, the paragraph and the
      // actions on one ruler.
      //
      // `min-h-full` rather than `h-full`. The block centres inside the height
      // it is given so it does not cling to the top bar above a band of empty
      // background — but a fixed 100% also CLIPPED it at the root boundary,
      // where the parent is a bare flex column and the copy is at its longest.
      className="mx-auto grid min-h-full w-full max-w-measure content-center justify-items-start gap-stack-normal py-8 md:py-9 lg:gap-stack-loose"
    >
      <span
        data-error-icon=""
        aria-hidden="true"
        className={[
          'inline-grid h-8 w-8 place-items-center rounded-md border md:h-[3.5rem] md:w-[3.5rem]',
          // One elevation above the page, so it reads as a state marker rather
          // than as decoration.
          kind === 'server'
            ? 'border-danger bg-danger-soft text-danger'
            : 'border-border bg-surface text-ink-muted',
          '[&>svg]:h-5 [&>svg]:w-5 md:[&>svg]:h-[1.75rem] md:[&>svg]:w-[1.75rem]',
        ].join(' ')}
      >
        {glyph}
      </span>

      {/*
        `tabIndex={-1}` exists for the programmatic focus above and puts the
        heading in no tab order of its own — which is also why it carries no
        focus indicator. The design system's ring says «you can act on this»,
        and 2.4.7 governs keyboard-OPERABLE components; a heading that receives
        focus once, from script, to be announced is neither. Both halves of the
        ring are suppressed, because the base layer draws it as an outline AND
        a box-shadow and cancelling only the outline leaves a black box.
      */}
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="m-0 text-h1 font-bold focus:shadow-none focus:outline-none"
      >
        {heading}
      </h1>

      <p className="m-0 text-ink-muted">{body}</p>

      <div data-error-actions="" className="flex w-full flex-wrap gap-3">
        {actions}
      </div>

      {footnote}
    </section>
  );
}
